import { useCallback, useRef } from "react";

/**
 * Shared "single active override" ref pattern used by contexts that let a
 * child component temporarily intercept a back-navigation action (native
 * device back button, in-page Link back-arrow, etc.) without prop drilling.
 *
 * Only one override can be registered at a time — the last caller to
 * register wins. Callers are expected to clear their own override on
 * unmount. `invokeOverride` returns `false` (and is a no-op) when nothing
 * is registered, matching the "let the caller fall back to default
 * navigation" contract used by both NativeBridgeContext and
 * CheckoutNavigationContext.
 */
export function useBackOverrideRef() {
  const overrideRef = useRef<(() => boolean) | null>(null);

  const setOverride = useCallback((overrideFn: () => boolean) => {
    overrideRef.current = overrideFn;
  }, []);

  const clearOverride = useCallback(() => {
    overrideRef.current = null;
  }, []);

  const invokeOverride = useCallback((): boolean => {
    return overrideRef.current?.() ?? false;
  }, []);

  return { setOverride, clearOverride, invokeOverride };
}

